import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

import Layout from "@components/Layout";
import Spinner from "@components/Spinner";
import Button from "@components/Button";
import { Column, Row as FlexRow } from "@components/Flex";
import { useLang } from "@src/hooks/useLang";
import { useStores } from "@stores";
import { ROUTES } from "@stores/AccountStore";
import { useSeasonsVM } from "@stores/SeasonStore";
import { useFarmsVM } from "@stores/FarmStore";
import { track, EVENTS } from "@src/utils/analytics";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import {
  reportsService,
  generateHarvestReport,
  type ReportSummary,
} from "@services/reportsService";

const Wrap = styled.div`
  padding: 20px 24px;
  overflow: auto;
  height: 100%;
  box-sizing: border-box;
  font-family: Nunito Sans, sans-serif;
`;

const Empty = styled.div`
  color: #899093;
  font-size: 15px;
  max-width: 420px;
  margin: 48px auto;
  text-align: center;
  line-height: 1.5;
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 900px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border: 1px solid #eceef0;
  border-radius: 10px;
  padding: 14px 18px;
  transition: 0.15s;
  &:hover {
    box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
  }
`;

const Info = styled.div`
  min-width: 0;
  cursor: pointer;
  flex: 1;
`;

const RTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: #2b2f38;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RMeta = styled.div`
  font-size: 12px;
  color: #899093;
  margin-top: 3px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const BtnLink = styled.a`
  text-decoration: none;
  font-weight: 700;
  font-size: 13px;
  padding: 7px 12px;
  border-radius: 7px;
  background: #f1f6ec;
  color: #4a7c2e;
  cursor: pointer;
  transition: 0.15s;
  &:hover {
    background: #e3eed7;
  }
`;

const Btn = styled.button`
  border: none;
  font-weight: 700;
  font-size: 13px;
  padding: 7px 12px;
  border-radius: 7px;
  background: #f1f6ec;
  color: #4a7c2e;
  cursor: pointer;
  transition: 0.15s;
  &:hover {
    background: #e3eed7;
  }
`;

const DeleteBtn = styled(Btn)`
  background: #faecec;
  color: #b54a4a;
  &:hover {
    background: #f3dada;
  }
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60vh;
`;

// ── Harvest report generator ────────────────────────────────
const SectionCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.2);
  border-radius: 12px;
  padding: 28px;
  max-width: 600px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px;
  }
`;

const SectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 18px;
  color: #494c5b;
  margin-bottom: 8px;
`;

const Description = styled.div`
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #899093;
  margin-bottom: 24px;
`;

const SeasonSelect = styled.select`
  height: 40px;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  padding: 0 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  background: #ffffff;
  cursor: pointer;
  min-width: 200px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #68ad18;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const Label = styled.label`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #899093;
  margin-bottom: 6px;
`;

const SpinnerEl = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ListTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #494c5b;
  margin-bottom: 12px;
`;

const ReportsScreen: React.FC = () => {
  const lang = useLang();
  const t = (lang as any).reports ?? {};
  const { settingsStore } = useStores();
  const seasonsVM = useSeasonsVM();
  const farmsVM = useFarmsVM();
  const [reports, setReports] = useState<ReportSummary[] | null>(null);

  const [selectedSeasonId, setSelectedSeasonId] = useState<string | undefined>(
    seasonsVM.currentSeasonId
  );
  // "" means all farms (no farm filter on the report)
  const [selectedFarmId, setSelectedFarmId] = useState<string>("");
  const [generating, setGenerating] = useState(false);

  // Update selected season when the store changes and we haven't chosen yet
  useEffect(() => {
    if (!selectedSeasonId && seasonsVM.currentSeasonId) {
      setSelectedSeasonId(seasonsVM.currentSeasonId);
    }
  }, [seasonsVM.currentSeasonId, selectedSeasonId]);

  const handleGenerate = useCallback(async () => {
    if (!selectedSeasonId) return;

    setGenerating(true);
    try {
      const farmId = selectedFarmId || undefined;
      const blob = await generateHarvestReport(
        selectedSeasonId,
        settingsStore.units,
        farmId
      );
      saveAs(blob, `harvest-report-${selectedSeasonId}.pdf`);
      track(EVENTS.HARVEST_REPORT_GENERATED, {
        seasonId: selectedSeasonId,
        farmId: farmId ?? "all",
      });
      toast.success(lang.analytics.downloadReady);
    } catch (e: any) {
      const msg = e?.message ?? lang.analytics.errorMessage;
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [selectedSeasonId, selectedFarmId, settingsStore.units, lang]);

  const formatSeasonLabel = (season: { from: Date; to: Date }): string => {
    const opts: Intl.DateTimeFormatOptions = { month: "short", year: "numeric" };
    const from = season.from.toLocaleDateString("en-US", opts);
    const to = season.to.toLocaleDateString("en-US", opts);
    return `${from} — ${to}`;
  };

  const load = () => {
    reportsService
      .listReports()
      .then((r) => setReports(r.reports))
      .catch(() => setReports([]));
  };

  useEffect(load, []);

  const openReport = (id: string) => {
    window.open(ROUTES.REPORTS + "/" + id, "_blank", "noopener");
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t.deleteConfirm ?? "Delete this report?")) return;
    await reportsService.deleteReport(id);
    setReports((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
  };

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <Layout title={lang.analytics?.reportsTitle ?? "Reports"}>
      <Wrap>
        <SectionCard>
          <SectionTitle>{lang.analytics.harvestReportTitle}</SectionTitle>
          <Description>{lang.analytics.harvestReportDescription}</Description>

          <Column style={{ gap: 16 }}>
            <Column style={{ gap: 4 }}>
              <Label>{lang.analytics.seasonLabel}</Label>
              <SeasonSelect
                value={selectedSeasonId ?? ""}
                onChange={(e) => setSelectedSeasonId(e.target.value)}
              >
                {seasonsVM.seasons.map((s) => (
                  <option key={s.id} value={s.id}>
                    {formatSeasonLabel(s)}
                  </option>
                ))}
              </SeasonSelect>
            </Column>

            <Column style={{ gap: 4 }}>
              <Label>{lang.menu.farmTitle}</Label>
              <SeasonSelect
                value={selectedFarmId}
                onChange={(e) => setSelectedFarmId(e.target.value)}
              >
                <option value="">{lang.farms.allFarms}</option>
                {farmsVM.farms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </SeasonSelect>
            </Column>

            <FlexRow>
              <Button
                kind="primary"
                onClick={handleGenerate}
                disabled={generating || !selectedSeasonId}
                style={{ maxWidth: 220 }}
              >
                {generating && <SpinnerEl />}
                {generating
                  ? lang.analytics.generating
                  : lang.analytics.generateButton}
              </Button>
            </FlexRow>
          </Column>
        </SectionCard>

        <ListTitle>{t.screenTitle ?? "Statistical Reports"}</ListTitle>

        {reports === null ? (
          <Center>
            <Spinner />
          </Center>
        ) : reports.length === 0 ? (
          <Empty>
            {t.empty ??
              "No reports yet. Open a field, compare two layers, and generate a statistical report."}
          </Empty>
        ) : (
          <ListWrap>
            {reports.map((r) => (
              <Row key={r.id}>
                <Info onClick={() => openReport(r.id)}>
                  <RTitle>{r.title}</RTitle>
                  <RMeta>
                    {(t.field ?? "Field")}: {r.fieldName ?? "—"} · {fmtDate(r.createdAt)}
                  </RMeta>
                </Info>
                <Actions>
                  <BtnLink onClick={() => openReport(r.id)}>{t.open ?? "Open"}</BtnLink>
                  <BtnLink
                    href={reportsService.reportPdfUrl(r.id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t.download ?? "PDF"}
                  </BtnLink>
                  <DeleteBtn onClick={() => handleDelete(r.id)}>
                    {t.delete ?? "Delete"}
                  </DeleteBtn>
                </Actions>
              </Row>
            ))}
          </ListWrap>
        )}
      </Wrap>
    </Layout>
  );
};

export default observer(ReportsScreen);
